import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, IconButton, Tooltip, Pagination, FormControl, Select, MenuItem, Switch,
  FormControlLabel, Alert, Skeleton,
} from '@mui/material';
import {
  Visibility, VisibilityOff, Delete, Refresh,
  Error as ErrorIcon, Warning as WarningIcon, Info as InfoIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { alertasService } from '../services/api';

const severidadeConfig: Record<string, { color: string; bg: string; icon: React.ReactElement; label: string }> = {
  critico: { color: '#DC2626', bg: '#FEE2E2', icon: <ErrorIcon sx={{ fontSize: 16 }} />, label: 'Critico' },
  alto: { color: '#D97706', bg: '#FEF3C7', icon: <WarningIcon sx={{ fontSize: 16 }} />, label: 'Alto' },
  informativo: { color: '#2563EB', bg: '#DBEAFE', icon: <InfoIcon sx={{ fontSize: 16 }} />, label: 'Informativo' },
};

const statusLabels: Record<string, string> = {
  novo: 'Novo',
  enviado: 'Enviado',
  lido: 'Lido',
  descartado: 'Descartado',
};

const Alertas: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const [tab, setTab] = useState(0);

  // Historico
  const [history, setHistory] = useState<any[]>([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histPage, setHistPage] = useState(1);
  const [histTotal, setHistTotal] = useState(0);
  const [histTotalPages, setHistTotalPages] = useState(0);
  const [filterSeveridade, setFilterSeveridade] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Preferencias
  const [prefs, setPrefs] = useState<any[]>([]);
  const [prefsLoading, setPrefsLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    if (!municipioAtivo?.id) return;
    setHistLoading(true);
    try {
      const data = await alertasService.getHistorico(
        municipioAtivo.id, histPage, 20,
        filterSeveridade || undefined,
        filterStatus || undefined,
      );
      setHistory(data.data || []);
      setHistTotal(data.total);
      setHistTotalPages(data.totalPages);
    } catch {
      setHistory([]);
    } finally {
      setHistLoading(false);
    }
  }, [municipioAtivo?.id, histPage, filterSeveridade, filterStatus]);

  const fetchPrefs = useCallback(async () => {
    if (!municipioAtivo?.id) return;
    setPrefsLoading(true);
    try {
      const data = await alertasService.getPreferencias(municipioAtivo.id);
      setPrefs(data);
    } catch {
      setPrefs([]);
    } finally {
      setPrefsLoading(false);
    }
  }, [municipioAtivo?.id]);

  useEffect(() => {
    if (tab === 0) fetchHistory();
    else fetchPrefs();
  }, [tab, fetchHistory, fetchPrefs]);

  const handleStatusChange = async (id: number, status: string) => {
    try {
      await alertasService.updateStatus(id, status, municipioAtivo!.id);
      fetchHistory();
    } catch {}
  };

  const handleTogglePref = async (pref: any, field: 'habilitado' | 'email_habilitado') => {
    if (!municipioAtivo?.id) return;
    try {
      await alertasService.updatePreferencia({
        alerta_tipo_id: pref.alerta_tipo_id,
        municipio_id: municipioAtivo.id,
        habilitado: field === 'habilitado' ? !pref.habilitado : pref.habilitado,
        email_habilitado: field === 'email_habilitado' ? !pref.email_habilitado : pref.email_habilitado,
      });
      fetchPrefs();
    } catch {}
  };

  if (!municipioAtivo) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Selecione um municipio para ver os alertas.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#24252E', fontSize: '1.5rem' }}>
            Alertas
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: '#7A7B8E', mt: 0.5 }}>
            Historico e preferencias de notificacoes
          </Typography>
        </Box>
        <Tooltip title="Atualizar">
          <IconButton onClick={tab === 0 ? fetchHistory : fetchPrefs} sx={{ color: '#7A7B8E' }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}>
          <Tab label="Historico" sx={{ textTransform: 'none', fontWeight: 500 }} />
          <Tab label="Preferencias" sx={{ textTransform: 'none', fontWeight: 500 }} />
        </Tabs>

        {/* Tab 0: Historico */}
        {tab === 0 && (
          <Box>
            {/* Filtros */}
            <Box sx={{ display: 'flex', gap: 2, p: 2, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={filterSeveridade}
                  onChange={(e) => { setFilterSeveridade(e.target.value); setHistPage(1); }}
                  displayEmpty
                  sx={{ fontSize: '0.8125rem' }}
                >
                  <MenuItem value="">Todas severidades</MenuItem>
                  <MenuItem value="critico">Critico</MenuItem>
                  <MenuItem value="alto">Alto</MenuItem>
                  <MenuItem value="informativo">Informativo</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={filterStatus}
                  onChange={(e) => { setFilterStatus(e.target.value); setHistPage(1); }}
                  displayEmpty
                  sx={{ fontSize: '0.8125rem' }}
                >
                  <MenuItem value="">Todos status</MenuItem>
                  <MenuItem value="novo">Novo</MenuItem>
                  <MenuItem value="enviado">Enviado</MenuItem>
                  <MenuItem value="lido">Lido</MenuItem>
                  <MenuItem value="descartado">Descartado</MenuItem>
                </Select>
              </FormControl>
              <Typography sx={{ fontSize: '0.8125rem', color: '#7A7B8E', alignSelf: 'center', ml: 'auto' }}>
                {histTotal} alerta{histTotal !== 1 ? 's' : ''}
              </Typography>
            </Box>

            {/* Tabela */}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#7A7B8E' }}>Severidade</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#7A7B8E' }}>Titulo</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#7A7B8E' }}>Resumo</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#7A7B8E' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#7A7B8E' }}>Data</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#7A7B8E' }} align="right">Acoes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {histLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 6 }).map((__, j) => (
                        <TableCell key={j}><Skeleton /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : history.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#7A7B8E' }}>
                      Nenhum alerta encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  history.map((alert) => {
                    const cfg = severidadeConfig[alert.severidade] || severidadeConfig.informativo;
                    return (
                      <TableRow key={alert.id} hover>
                        <TableCell>
                          <Chip
                            icon={cfg.icon}
                            label={cfg.label}
                            size="small"
                            sx={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: '0.7rem', '& .MuiChip-icon': { color: cfg.color } }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8125rem', fontWeight: 500, maxWidth: 200 }}>{alert.titulo}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', color: '#7A7B8E', maxWidth: 300 }}>
                          <Typography noWrap sx={{ fontSize: 'inherit', color: 'inherit' }}>
                            {alert.resumo_ia || alert.resumo}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={statusLabels[alert.status] || alert.status}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', color: '#7A7B8E', whiteSpace: 'nowrap' }}>
                          {new Date(alert.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </TableCell>
                        <TableCell align="right">
                          {alert.status !== 'lido' && (
                            <Tooltip title="Marcar como lido">
                              <IconButton size="small" onClick={() => handleStatusChange(alert.id, 'lido')}>
                                <Visibility sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {alert.status !== 'descartado' && (
                            <Tooltip title="Descartar">
                              <IconButton size="small" onClick={() => handleStatusChange(alert.id, 'descartado')}>
                                <VisibilityOff sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {/* Paginacao */}
            {histTotalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <Pagination
                  count={histTotalPages}
                  page={histPage}
                  onChange={(_, v) => setHistPage(v)}
                  color="primary"
                  size="small"
                />
              </Box>
            )}
          </Box>
        )}

        {/* Tab 1: Preferencias */}
        {tab === 1 && (
          <Box>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: '0.8125rem', color: '#7A7B8E' }}>
                Configure quais alertas deseja receber e se devem ser enviados por email.
              </Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#7A7B8E' }}>Categoria</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#7A7B8E' }}>Alerta</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#7A7B8E' }}>Descricao</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#7A7B8E' }} align="center">Ativo</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#7A7B8E' }} align="center">Email</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {prefsLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 5 }).map((__, j) => (
                        <TableCell key={j}><Skeleton /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : prefs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#7A7B8E' }}>
                      Nenhuma preferencia disponivel
                    </TableCell>
                  </TableRow>
                ) : (
                  prefs.map((pref) => {
                    const cfg = severidadeConfig[pref.categoria] || severidadeConfig.informativo;
                    return (
                      <TableRow key={pref.codigo} hover>
                        <TableCell>
                          <Chip
                            icon={cfg.icon}
                            label={cfg.label}
                            size="small"
                            sx={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: '0.7rem', '& .MuiChip-icon': { color: cfg.color } }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>{pref.nome}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', color: '#7A7B8E' }}>{pref.descricao}</TableCell>
                        <TableCell align="center">
                          <Switch
                            size="small"
                            checked={pref.habilitado}
                            onChange={() => handleTogglePref(pref, 'habilitado')}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Switch
                            size="small"
                            checked={pref.email_habilitado}
                            onChange={() => handleTogglePref(pref, 'email_habilitado')}
                            disabled={!pref.habilitado}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Alertas;
